import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTransaction } from '@/api/transactions/use-transactions';
import { useCustomerTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

const transactionSchema = z.object({
    trading_account_id: z.string().min(1, 'Trading account is required'),
    type: z.enum(['deposit', 'withdrawal', 'bonus']),
    amount: z.number().positive('Amount must be positive'),
    currency: z.string().min(1, 'Currency is required'),
    payment_method: z.string().optional(),
    reference_number: z.string().optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
});

type TransactionFormData = z.infer<typeof transactionSchema>;

interface TransactionModalProps {
    open: boolean;
    onClose: () => void;
    customerId: string;
    onSuccess: () => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({
    open,
    onClose,
    customerId,
    onSuccess,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const createTransactionMutation = useCreateTransaction(customerId);

    const { data: tradingAccountsData, refetch: refetchTradingAccounts } =
        useCustomerTradingAccounts(customerId);
    const tradingAccounts = tradingAccountsData?.accounts || [];

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm<TransactionFormData>({
        resolver: zodResolver(transactionSchema),
        defaultValues: {
            type: 'deposit',
            amount: 0,
            currency: '',
            payment_method: '',
            reference_number: '',
            description: '',
            notes: '',
        },
    });

    const selectedType = watch('type');
    const selectedAccountId = watch('trading_account_id');

    // Set currency when trading account is selected
    React.useEffect(() => {
        if (selectedAccountId) {
            const selectedAccount = tradingAccounts.find(acc => acc.id === selectedAccountId);
            if (selectedAccount) {
                setValue('currency', selectedAccount.base_currency);
            }
        }
    }, [selectedAccountId, tradingAccounts, setValue]);

    const onSubmit = async (data: TransactionFormData) => {
        setIsSubmitting(true);
        try {
            await createTransactionMutation.mutateAsync({
                ...data,
                customer_id: customerId,
            });
            // Invalidate and refetch trading accounts to update balance
            queryClient.invalidateQueries({
                queryKey: ['customer-trading-accounts', customerId],
            });
            await refetchTradingAccounts();
            enqueueSnackbar('Transaction created successfully', { variant: 'success' });
            reset();
            onSuccess();
        } catch (error) {
            enqueueSnackbar('Failed to create transaction', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create New Transaction</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">Type</Label>
                            <Select
                                value={selectedType}
                                onValueChange={value =>
                                    setValue('type', value as 'deposit' | 'withdrawal' | 'bonus')
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="deposit">Deposit</SelectItem>
                                    <SelectItem value="withdrawal">Withdrawal</SelectItem>
                                    <SelectItem value="bonus">Bonus</SelectItem>
                                </SelectContent>
                            </Select>
                            {errors.type && (
                                <p className="text-sm text-destructive">{errors.type.message}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="trading_account_id">Trading Account</Label>
                            <Select
                                value={selectedAccountId}
                                onValueChange={value => setValue('trading_account_id', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tradingAccounts.map(account => (
                                        <SelectItem key={account.id} value={account.id}>
                                            {account.base_currency} - #
                                            {account.account_number || 'N/A'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.trading_account_id && (
                                <p className="text-sm text-destructive">
                                    {errors.trading_account_id.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="amount">Amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                {...register('amount', { valueAsNumber: true })}
                                placeholder="Enter amount"
                            />
                            {errors.amount && (
                                <p className="text-sm text-destructive">{errors.amount.message}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="currency">Currency</Label>
                            <Input {...register('currency')} placeholder="Currency" readOnly />
                            {errors.currency && (
                                <p className="text-sm text-destructive">
                                    {errors.currency.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="payment_method">Payment Method</Label>
                        <Input
                            {...register('payment_method')}
                            placeholder="e.g., Bank Transfer, Credit Card, etc."
                        />
                        {errors.payment_method && (
                            <p className="text-sm text-destructive">
                                {errors.payment_method.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="reference_number">Reference Number</Label>
                        <Input
                            {...register('reference_number')}
                            placeholder="Transaction reference number"
                        />
                        {errors.reference_number && (
                            <p className="text-sm text-destructive">
                                {errors.reference_number.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Input
                            {...register('description')}
                            placeholder="Brief description of the transaction"
                        />
                        {errors.description && (
                            <p className="text-sm text-destructive">{errors.description.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="notes">Notes</Label>
                        <Textarea
                            {...register('notes')}
                            placeholder="Additional notes (optional)"
                            rows={3}
                        />
                        {errors.notes && (
                            <p className="text-sm text-destructive">{errors.notes.message}</p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create Transaction'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionModal;
